import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { CascadeSelectModule } from 'primeng/cascadeselect';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectFilterOptions, Select } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { TagModule } from 'primeng/tag';
import { Observable, Subscription } from 'rxjs';
import { CollapsibleDirective } from 'src/app/core/components/pages-container/directives/collapsibleDirective.directive';
import {
  ActionsPlacement,
  FilterInputTypes,
  IFilterInput,
} from '../../interfaces/filter-input.interface';
import { Value } from '../../interfaces/value.interface';
import { ButtonComponent } from '../button/button.component';
import { MaxDatePipe } from './pipes/maxDate.pipe';
import { DebounceInputDirective } from '../../directives/debounce-input.directive';
import { AdjustDropdownHeightDirective } from '../../directives/adjust-dropdown-height.directive';
import { InputNumberModule } from 'primeng/inputnumber';

export interface RemoteConfigurations<T = any> {
  url: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  headers?: { [key: string]: string };
  body: T;
}

@Component({
  selector: 'st-grid-filters',
  templateUrl: './filters-grid.component.html',
  styleUrls: ['./filters-grid.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    Select,
    MultiSelectModule,
    CheckboxModule,
    DatePicker,
    DebounceInputDirective,
    AdjustDropdownHeightDirective,
    TagModule,
    CollapsibleDirective,
    CascadeSelectModule,
    ButtonComponent,
    MaxDatePipe,
    InputNumberModule,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class GridFiltersComponent implements OnInit, OnChanges, OnDestroy {
  @Input() remoteFilters: boolean = false;
  @Input() remoteConfigurations!: RemoteConfigurations;
  @Input() filters: IFilterInput[] = [];
  @Input() sectionTitle: string = '';
  @Input() expanded: boolean = false;
  @Input() filterActionsPlacement: ActionsPlacement = 'end';
  @Input() AllowUpdateFilterControls = false;
  @Input() updatedControls!: IFilterInput | IFilterInput[];
  @Input() numberOfResults: number = 0;
  @Input() filterToggleIcon: string = 'icon-funnel-simple';
  @Input() customHeaderContentWithFilterButton: boolean = false;
  @Input() showButtons: boolean = true;

  /**Tag configuration */
  @Input() showTitleTag: boolean = false;
  @Input() tagSeverity:
    | 'secondary'
    | 'success'
    | 'info'
    | 'progress'
    | 'warning'
    | 'pending'
    | 'help'
    | 'danger' = 'secondary';
  @Input() tagValue: string = 'secondary';

  /** main buttons text*/
  @Input() filterToggleText: string = 'Filters';
  @Input() clearButtonText: string = 'Clear';
  @Input() ApplyButtonText: string = 'Apply';

  /** This input is to select between the search and filters buttons designs
   * should not be needed after the design is settled on it
   */
  @Input() noGapFilters: boolean = true;

  @Output() public applyFilters: EventEmitter<{
    filters: any;
    search: string;
  }> = new EventEmitter<{ filters: any; search: string }>();
  @Output() public filtersCleared: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() public showHideFilters: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() public searchTriggered: EventEmitter<{
    filters: any;
    search: string;
  }> = new EventEmitter<{ filters: any; search: string }>();
  @Output() public errorHappen: EventEmitter<string> =
    new EventEmitter<string>();
  @Input() requireCompleteDateRange: boolean = false;

  public filterInputs: IFilterInput[] = [];
  public filterForm!: FormGroup;
  public showFilter: WritableSignal<boolean> = signal(false);
  public isApplied: WritableSignal<boolean> = signal(false);
  public searchModel: string = '';
  public filterResults: Value[] = [];
  public isApplyButtonDisabled: boolean = true;
  public formClosed = signal(false);

  private httpSubscription?: Subscription;
  private valuesChangeSubscription?: Subscription;

  isButtonColumnLayout = false;

  constructor(
    private readonly fb: FormBuilder,
    private readonly http: HttpClient,
    private readonly elRef: ElementRef,
  ) {}

  ngOnInit() {
    this.showFilter.set(this.expanded);
    if (this.remoteFilters) {
      this.httpSubscription = this.makeHttpRequest().subscribe((filters) => {
        this.filterInputs = filters;
        this.createForm();
        this.subscribeToFormChanges();
      });
    } else {
      this.filterInputs = this.filters;
      this.createForm();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.AllowUpdateFilterControls) {
      this.updateFilterControls();
    }
  }

  ngOnDestroy() {
    this.httpSubscription?.unsubscribe();
    this.valuesChangeSubscription?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize() {
    this.updateLayout();
  }

  private updateLayout() {
    const filterForm = this.elRef.nativeElement.querySelector('.filter-inputs');

    if (filterForm.offsetHeight > 40) {
      this.isButtonColumnLayout = true;
    } else {
      this.isButtonColumnLayout = false;
    }
  }

  showFilters() {
    this.showFilter.update((x) => !x);
    this.showHideFilters.emit(this.showFilter());
  }

  search(event: string) {
    this.searchTriggered.emit({
      filters: this.prepareFormBody(),
      search: event,
    });
  }

  submit() {
    if (!this.validateRangeFields()) {
      return;
    }

    this.applyFilters.emit({
      filters: this.prepareFormBody(),
      search: this.searchModel,
    });
    this.filterResults = this.prepareFilterResult();
    this.showFilter.set(false);
    this.showHideFilters.emit(false);
    this.isApplied.set(true);
  }

  clearFilters() {
    this.filterForm.reset();
    this.filtersCleared.emit();
    this.isApplied.set(false);
    this.filterResults = [];
    this.numberOfResults = 0;
  }

  clearResults() {
    this.clearFilters();
    this.showFilter.set(false);
    if (!this.showFilter()) {
      this.showHideFilters.emit(true);
    }
  }

  private isDateRangeValid(value: any): boolean {
    return (
      value.ride_date_range_from !== null &&
      value.ride_date_range_from !== '' &&
      value.ride_date_range_to !== null &&
      value.ride_date_range_to !== ''
    );
  }

  customFilterFunction(
    event: KeyboardEvent,
    options: SelectFilterOptions,
  ): void {
    console.log(event, options);

    if (options.filter) {
      options.filter(event);
    } else {
      console.error('Filter function is undefined.');
    }
  }

  removeSelectedFilter(controlName: string, index: number) {
    this.filterResults = this.filterResults.filter((_, i) => i !== index);

    this.filterForm.controls[controlName].reset();
    if (!this.filterResults.length) {
      this.clearFilters();
      if (!this.showFilter()) {
        this.showFilter.set(false);
        this.showHideFilters.emit(true);
      }
    }
    this.applyFilters.emit({
      filters: this.prepareFormBody(),
      search: this.searchModel,
    });
  }

  private subscribeToFormChanges() {
    this.valuesChangeSubscription = this.filterForm.valueChanges.subscribe(
      (_) => {
        this.isApplyButtonDisabled = !this.isFormChanged();
      },
    );
  }

  private validateRangeFields(): boolean {
    for (const filter of this.filterInputs) {
      if (
        filter.type === FilterInputTypes._timeRange ||
        filter.type === FilterInputTypes._dateRange
      ) {
        const fromControlName = filter.rangeFilterReferences?.[0].controlName;
        const toControlName = filter.rangeFilterReferences?.[1].controlName;
        const fromValue = this.filterForm.get(
          `${filter.controlName}.${fromControlName}`,
        )?.value;
        const toValue = this.filterForm.get(
          `${filter.controlName}.${toControlName}`,
        )?.value;

        if (fromValue && toValue && fromValue > toValue) {
          this.errorHappen.emit(
            `'To' value should be greater than 'From' value for ${filter.resultLabel}`,
          );
          return false;
        }
      }
    }
    return true;
  }

  /**
   *  Handel Time range to Validation if the type is timeRange check if range from and range to is passed
   *
   * @param filter - Input filter from input list
   */

  private validateTimeRange(filter: IFilterInput) {
    if (filter.type === FilterInputTypes._timeRange) {
      if (
        !filter.rangeFilterReferences ||
        filter.rangeFilterReferences.length !== 2 ||
        filter.rangeFilterReferences[0].type !==
          FilterInputTypes._timeRangeFrom ||
        filter.rangeFilterReferences[1].type !== FilterInputTypes._timeRangeTo
      ) {
        throw new Error(
          `Invalid rangeFilterReferences for filter type ${filter.type} with controlName ${filter.controlName}`,
        );
      }
    }
  }

  /**
   *  Handel Date range to Validation if the type is dateRange check if range from and range to is passed
   *
   * @param filter - Input filter from input list
   */

  private validateDateRange(filter: IFilterInput) {
    if (filter.type === FilterInputTypes._dateRange) {
      if (
        !filter.rangeFilterReferences ||
        filter.rangeFilterReferences.length !== 2 ||
        filter.rangeFilterReferences[0].type !==
          FilterInputTypes._dateRangeFrom ||
        filter.rangeFilterReferences[1].type !== FilterInputTypes._dateRangeTo
      ) {
        throw new Error(
          `Invalid rangeFilterReferences for filter type ${filter.type} with controlName ${filter.controlName}`,
        );
      }
    }
  }

  /**
   *  Handel DateTime to Validation do not pass single time and time range or single or date range just one of the single or range,
   *  also handel the duplicate date && time filter
   *
   * @param filter - Input filter from input list
   */

  private validateSingleTypes(filter: IFilterInput) {
    // Allow multiple single date/time filters; only prevent mixing
    // single and range types of the same category in one configuration.
    const hasDateRange = this.filterInputs.some(
      (f) => f.type === FilterInputTypes._dateRange,
    );
    const hasTimeRange = this.filterInputs.some(
      (f) => f.type === FilterInputTypes._timeRange,
    );

    if (
      (filter.type === FilterInputTypes._date && hasDateRange) ||
      (filter.type === FilterInputTypes._time && hasTimeRange)
    ) {
      throw new Error(
        `Cannot use both single and range types for filter with controlName ${filter.controlName}`,
      );
    }
  }

  /**
   *  Handel filter form filter inputs also include:
   *  - validate time range.
   *  - validate date range.
   *  - validate date time single type.
   *  - validate if the type dropdown or multiSelect check if the options and option label available.
   */

  private createForm() {
    let group: any = {};

    this.filterInputs.forEach((filter) => {
      this.validateTimeRange(filter);
      this.validateDateRange(filter);
      this.validateSingleTypes(filter);

      const defaultValue = filter.defaultValue ?? '';

      if (
        filter.type === FilterInputTypes._dropdown ||
        filter.type === FilterInputTypes._multiSelect ||
        filter.type === FilterInputTypes._cascadeSelect
      ) {
        // Ensure options and optionLabel are present
        if (!filter.options || !filter.optionLabel) {
          throw new Error(
            `Missing options or optionLabel for filter type ${filter.type} with controlName ${filter.controlName}`,
          );
        }

        // Determine the default value
        const defaultValue = this.determineDefaultValue(filter);

        // Create form control with default value and subscribe to changes
        const control = this.fb.control({
          value: defaultValue,
          disabled: !!filter.disabled,
        });

        control.valueChanges.subscribe((value) => {
          if (filter.onChange) {
            filter.onChange(value);
          }
        });

        group[filter.controlName] = control;
      } else if (
        filter.type === FilterInputTypes._timeRange ||
        filter.type === FilterInputTypes._dateRange
      ) {
        const subGroup: { [key: string]: any } = {};

        // Process each range filter reference
        filter.rangeFilterReferences?.forEach((rangeFilter) => {
          const defaultValue = rangeFilter.defaultValue;
          subGroup[rangeFilter.controlName] = this.fb.control({
            value: defaultValue,
            disabled: !!rangeFilter.disabled,
          });
        });

        // Create a FormGroup for the range filter
        group[filter.controlName] = this.fb.group(subGroup);
      } else {
        group[filter.controlName] = {
          value: defaultValue,
          disabled: !!filter.disabled,
        };
      }
    });

    this.filterForm = this.fb.group(group);
    this.subscribeToFormChanges();
  }

  public determineDefaultValue(filter: any): any {
    switch (filter.type) {
      case FilterInputTypes._cascadeSelect:
        return this.findCascadeSelectDefaultValue(filter);

      case FilterInputTypes._dropdown:
        return (
          filter.options.find(
            (item: { value: any }) => item.value === filter.defaultValue,
          ) ?? ''
        );

      case FilterInputTypes._multiSelect:
        return filter.options.filter(
          (item: { value: any }) =>
            Array.isArray(filter.defaultValue) &&
            filter.defaultValue.includes(item.value),
        );

      default:
        return '';
    }
  }

  public findCascadeSelectDefaultValue(filter: any): any {
    for (const option of filter.options) {
      const foundItem = option.items.find(
        (item: { value: any }) => item.value === filter.defaultValue,
      );
      if (foundItem) {
        return foundItem;
      }
    }
    return '';
  }

  /**
   * Extract the response body from the form builder to use it with apply action or search also with result chips.
   */

  private prepareFormBody(): { [key: string]: any } {
    const body: { [key: string]: any } = {};
    Object.entries(this.filterForm.value).forEach(([key, value]) => {
      if (
        this.filters.some(
          (x) => key.includes(x.controlName) && x.type === 'checkbox',
        )
      ) {
        body[key] = (value as Array<boolean>)?.length === 1;
      } else if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        Object.entries(value).length > 1
      ) {
        value = Object.entries(value).reduce(
          (acc, [key, value]) => {
            acc[key] = value ?? '';
            return acc;
          },
          {} as Record<string, string>,
        );
        body[key] = value ?? '';
      } else {
        body[key] = value ?? '';
      }
    });

    return body;
  }

  private fillFilterResult(
    currentEntry: any,
    filterResults: Value[],
    currentFilter: IFilterInput,
  ) {
    const isWhitespaceString = (str: string) =>
      str.replaceAll(/\s/g, '').length;
    if (currentEntry) {
      const [key, value] = currentEntry;
      if (typeof value === 'boolean' && value) {
        filterResults.push({
          label: currentFilter.resultLabel,
          value: true,
          clearable: currentFilter.resultClearable ?? true,
          moreInformation: {
            controlName: currentFilter.controlName,
          },
        });
      } else if (Array.isArray(value) && value.length) {
        const resultArrayString = `${value[0].label}${
          value.length > 1 ? '...+' + (value.length - 1) : ''
        }`;
        filterResults.push({
          label: currentFilter.resultLabel,
          value: resultArrayString,
          clearable: currentFilter.resultClearable ?? true,
          moreInformation: {
            controlName: currentFilter.controlName,
          },
        });
      } else if (typeof value === 'object' && value?.label) {
        filterResults.push({
          label: currentFilter.resultLabel,
          value: value.label,
          clearable: currentFilter.resultClearable ?? true,
          moreInformation: {
            controlName: currentFilter.controlName,
          },
        });
      } else if (typeof value === 'string' && isWhitespaceString(value)) {
        filterResults.push({
          label: currentFilter.resultLabel,
          value: value,
          clearable: currentFilter.resultClearable ?? true,
          moreInformation: {
            controlName: currentFilter.controlName,
          },
        });
      } else if (typeof value === 'number') {
        let currentlabel = currentFilter.resultLabel;
        filterResults.push({
          label: currentlabel,
          value: value,
          clearable: currentFilter.resultClearable ?? true,
          moreInformation: {
            controlName: currentFilter.controlName,
          },
        });
      }
      return key;
    }
  }

  private prepareFilterResult() {
    const _filterResults: Value[] = [];
    const entries = Object.entries(this.prepareFormBody()).map(
      ([key, value]) => [key, value],
    );

    this.filters.forEach((filter) => {
      let currentEntry = entries.find(([key]) => key === filter.controlName);

      if (!currentEntry) return;

      switch (filter.type) {
        case FilterInputTypes._timeRange:
          if (filter.rangeFilterReferences?.length)
            this.handleTimeRange(filter, currentEntry);
          break;
        case FilterInputTypes._dateRange:
          if (filter.rangeFilterReferences?.length)
            this.handleDateRange(filter, currentEntry);
          break;
        case FilterInputTypes._time:
          this.handleTime(currentEntry);
          break;
        case FilterInputTypes._date:
          this.handleDate(currentEntry);
          break;
      }

      this.fillFilterResult(currentEntry, _filterResults, filter);
    });

    return _filterResults;
  }

  private formatTime(date: Date): string {
    return `${date.getHours()}:${
      date.getMinutes() < 10 ? '0' + date.getMinutes() : date.getMinutes()
    }`;
  }

  private formatDate(date: Date): string {
    return `${date.getMonth() + 1 < 10 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  private handleTimeRange(filter: any, currentEntry: any) {
    const fromControlName = filter.rangeFilterReferences[0].controlName;
    const toControlName = filter.rangeFilterReferences[1].controlName;
    const fromValue = currentEntry[1][fromControlName];
    const toValue = currentEntry[1][toControlName];

    let timeFrom;
    let timeTo;

    if (typeof fromValue === 'object') {
      timeFrom = new Date(fromValue);
    }

    if (typeof toValue === 'object') {
      timeTo = new Date(toValue);
    }

    currentEntry[1] = `${timeFrom ? this.formatTime(timeFrom) : ''} ${
      timeTo ? 'to ' + this.formatTime(timeTo) : ''
    }`;
  }

  private handleDateRange(filter: any, currentEntry: any) {
    const fromControlName = filter.rangeFilterReferences[0].controlName;
    const toControlName = filter.rangeFilterReferences[1].controlName;
    const fromValue = currentEntry[1][fromControlName];
    const toValue = currentEntry[1][toControlName];
    if (
      typeof fromValue === 'string' &&
      !fromValue.length &&
      typeof toValue === 'string' &&
      !toValue.length
    ) {
      return;
    }
    let dateFrom;
    let dateTo;
    if (typeof fromValue === 'object') {
      dateFrom = new Date(fromValue);
    }

    if (typeof toValue === 'object') {
      dateTo = new Date(toValue);
    }

    currentEntry[1] = `${dateFrom ? this.formatDate(dateFrom) : ''} ${
      dateTo ? 'to ' + this.formatDate(dateTo) : ''
    }`;
  }

  private handleTime(currentEntry: any) {
    if (typeof currentEntry[1] === 'string' && !currentEntry[1].length) {
      return;
    }
    if (typeof currentEntry[1] === 'object') {
      const time = new Date(currentEntry[1]);
      currentEntry[1] = this.formatTime(time);
    }
  }

  private handleDate(currentEntry: any) {
    if (typeof currentEntry[1] === 'string' && !currentEntry[1].length) {
      return;
    }
    if (typeof currentEntry[1] === 'object') {
      const date = new Date(currentEntry[1]);
      currentEntry[1] = this.formatDate(date);
    }
  }

  private isFormChanged(): boolean {
    // Handle requireCompleteDateRange logic
    if (this.requireCompleteDateRange) {
      const dateRangeValue = this.filterForm.value['dateRange'];
      if (
        dateRangeValue &&
        typeof dateRangeValue === 'object' &&
        dateRangeValue.startDate &&
        dateRangeValue.endDate
      ) {
        return this.isDateRangeValid(dateRangeValue);
      }
      return false;
    }

    // Handle additional required fields
    const requiredFieldsValid = this.filterInputs
      .filter((field) => field.required)
      .every((field) => {
        const value = this.filterForm.value[field.controlName];
        if (value === null || value === undefined || value === '') return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'object') {
          return Object.values(value).some((v) => v !== null && v !== '');
        }
        return true;
      });

    if (!requiredFieldsValid) {
      return false;
    }

    // General check for any changed values
    return Object.values(this.filterForm.value).some((value) => {
      if (value instanceof Date) {
        return true;
      }
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        if ('ride_date_range_from' in value && 'ride_date_range_to' in value) {
          return this.isDateRangeValid(value);
        }
        return Object.values(value).some(
          (subValue) => subValue !== null && subValue !== '',
        );
      } else if (Array.isArray(value)) {
        return value.length > 0;
      }
      return value !== null && value !== '';
    });
  }

  private makeHttpRequest(): Observable<IFilterInput[]> {
    if (!this.remoteConfigurations) {
      throw new Error(
        "Missing remote configurations please check it or make 'remoteFilters' false",
      );
    }

    const { url, method, headers, body } = this.remoteConfigurations;
    const httpOptions = {
      headers: new HttpHeaders(headers),
    };

    switch (method) {
      case 'GET':
        return this.http.get<IFilterInput[]>(url, httpOptions);
      case 'POST':
        return this.http.post<IFilterInput[]>(url, body, httpOptions);
      case 'PUT':
        return this.http.put<IFilterInput[]>(url, body, httpOptions);
      case 'DELETE':
        return this.http.delete<IFilterInput[]>(url, httpOptions);
      default:
        throw new Error('Unsupported HTTP method');
    }
  }

  private updateFilterControls() {
    if (this.updatedControls) {
      if (
        !Array.isArray(this.updatedControls) &&
        (this.updatedControls.type === FilterInputTypes._multiSelect ||
          this.updatedControls.type === FilterInputTypes._dropdown)
      ) {
        this.filterInputs.forEach((input) => {
          if (
            !Array.isArray(this.updatedControls) &&
            input.controlName === this.updatedControls.controlName
          ) {
            input.options = this.updatedControls.options;
            if (this.filterForm?.controls?.[input.controlName]) {
              this.filterForm.controls[input.controlName].reset();
            }
          }
        });
      } else if (
        !Array.isArray(this.updatedControls) &&
        !(
          this.updatedControls.type === FilterInputTypes._multiSelect ||
          this.updatedControls.type === FilterInputTypes._dropdown
        )
      ) {
        throw new Error(
          `Cannot update the filter with controlName ${this.updatedControls.controlName},just you can update the dropdowns and multiSelect type`,
        );
      } else if (
        Array.isArray(this.updatedControls) &&
        this.updatedControls.every(
          (input) =>
            input.type === FilterInputTypes._multiSelect ||
            input.type === FilterInputTypes._dropdown,
        )
      ) {
        this.updatedControls.forEach((input) => {
          if (
            this.filterInputs.some((x) => x.controlName === input.controlName)
          ) {
            const inputIndex = this.filterInputs.findIndex(
              (x) => x.controlName === input.controlName,
            );
            if (inputIndex !== -1) {
              this.filterInputs[inputIndex].options = input.options;
              if (this.filterForm?.controls?.[input.controlName]) {
                this.filterForm.controls[input.controlName].reset();
              }
            }
          }
        });
      } else if (
        Array.isArray(this.updatedControls) &&
        !this.updatedControls.every(
          (input) =>
            input.type === FilterInputTypes._multiSelect ||
            input.type === FilterInputTypes._dropdown,
        )
      ) {
        let notMatchControls = '';
        this.updatedControls.forEach((input, i) => {
          if (!(
            input.type === FilterInputTypes._multiSelect ||
            input.type === FilterInputTypes._dropdown
          )) {
            notMatchControls += i === 0 ? input.type : ', ' + input.type;
            throw new Error(
              `Cannot update the filter with controlName ${notMatchControls},just you can update the dropdowns and multiSelect type`,
            );
          }
        });
      }
    }
  }

  public onNumberInput(event: Event, filter: IFilterInput) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Allow empty string (so user can delete to retype)
    if (value === '') {
      this.filterForm
        .get(filter.controlName)
        ?.setValue(null, { emitEvent: false });
      return;
    }

    let numericValue = Number(value);

    // Enforce min/max if value is valid
    if (!Number.isNaN(numericValue)) {
      if (filter.minNumber !== undefined && numericValue < filter.minNumber) {
        numericValue = filter.minNumber;
      }
      if (filter.maxNumber !== undefined && numericValue > filter.maxNumber) {
        numericValue = filter.maxNumber;
      }

      // Update input and form
      this.filterForm
        .get(filter.controlName)
        ?.setValue(numericValue, { emitEvent: false });
      input.value = numericValue.toString();
    }
  }
}
